import LinearProgress from '@mui/material/LinearProgress';
import {styled} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {useReactTable, getCoreRowModel, getFilteredRowModel} from '@tanstack/react-table';
import React, {useState} from 'react';
import {useSelector} from 'react-redux';

import {pxToRem} from '../../utils';
import CustomTablePagination from '../CustomTablePagination/CustomTablePagination';

import TableBody from './TableBody';
import TableHead from './TableHead';

const StyledTableContainerDiv = styled('div')(({theme, tableHeight}) => ({
  fontSize: '12px',
  boxShadow: theme.palette.boxShadow.type4,
  tableLayout: 'fixed',
  backgroundColor: theme.palette.white,
  color: theme.palette.black,
  width: '100%',
  borderRadius: pxToRem(4),
  overflowX: 'auto',
  height: tableHeight ? `${tableHeight}vh` : '72vh',
}));

const StyledTable = styled('table')(({theme}) => ({
  width: '100%',
  display: 'table',
  borderSpacing: 0,
  borderCollapse: 'collapse',
}));

const StyledTypography = styled(Typography)(({theme, iserror}) => ({
  color: iserror === 'true' ? theme.palette.error.main : theme.palette.black,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
}));

const CustomTable = ({
  columns,
  dataQuery,
  tableKey,
  pageSize,
  pageSizeOptions,
  pageIndex,
  setPagination,
  filters,
  globalFilter,
  setFilters,
  setGlobalFilter,
  columnVisibility,
  allowDateRangeSelection,
  enablePagination = true,
  tableHeight,
}) => {
  const triggerUpdateTableData = useSelector((store) => store?.triggerUpdateTableData);

  const [openCustomDateSelection, setOpenCustomDateSelection] = useState(false);

  const tableInstance = useReactTable({
    data: dataQuery.data?.rows ?? [],
    pageCount: enablePagination ? Math.ceil(dataQuery.data?.totalCount / (pageSize || 10)) || 0 : 0,
    columns,
    initialState: enablePagination
      ? {
          pagination: {
            pageIndex,
            pageSize,
          },
        }
      : {},
    state: {
      columnVisibility,
      pagination: {pageSize, pageIndex},
      globalFilter,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    manualPagination: true,
  });

  return (
    <>
      {dataQuery.isLoading || dataQuery.isFetching ? <LinearProgress /> : null}
      <StyledTableContainerDiv tableHeight={tableHeight}>
        <StyledTable key={tableKey}>
          <TableHead
            tableInstance={tableInstance}
            filters={filters}
            setFilters={setFilters}
            openCustomDateSelection={openCustomDateSelection}
            setOpenCustomDateSelection={setOpenCustomDateSelection}
            allowDateRangeSelection={allowDateRangeSelection}
          />
          {dataQuery.status === 'success' && dataQuery?.data?.rows?.length > 0 ? (
            <TableBody
              rows={tableInstance.getRowModel().rows}
              columnVisibility={columnVisibility}
              triggerUpdateTableData={triggerUpdateTableData}
            />
          ) : null}
        </StyledTable>
        {dataQuery.status === 'error' ? (
          <StyledTypography variant="h5" iserror={'true'}>
            Error!
          </StyledTypography>
        ) : null}
        {dataQuery.status === 'success' && tableInstance.getRowModel()?.rows?.length === 0 ? (
          <StyledTypography variant="h5">No records found!</StyledTypography>
        ) : null}
      </StyledTableContainerDiv>
      {enablePagination ? (
        <CustomTablePagination
          totalcount={dataQuery.data?.totalCount || 0}
          tableInstance={tableInstance}
          setPagination={setPagination}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          globalFilter={globalFilter}
        />
      ) : null}
    </>
  );
};

export default CustomTable;
